import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { BoardCard, COLUMN_BAR_COLOR } from './BoardCard';
import type { BoardCard as BoardCardType, BoardColumnKey } from '../types';

interface BoardColumnProps {
  columnKey: BoardColumnKey;
  title: string;
  cards: BoardCardType[];
  onCardClick?: (card: BoardCardType) => void;
  /**
   * Ouverture du dossier autorisée pour cette carte ? (admin ou responsable
   * assigné). Une carte non ouvrable reste visible mais n'est pas cliquable.
   */
  canOpenCard?: (card: BoardCardType) => boolean;
  /** Rend le contrôle d'assignation d'une carte (admin uniquement). */
  renderAssign?: (card: BoardCardType) => ReactNode;
  /** Rend le contrôle de date de migration prévue d'une carte (admin uniquement). */
  renderPlanned?: (card: BoardCardType) => ReactNode;
}

export function BoardColumn({ columnKey, title, cards, onCardClick, canOpenCard, renderAssign, renderPlanned }: BoardColumnProps) {
  return (
    <div className="flex flex-col gap-3 min-w-0">
      {/* Column header */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className={cn('h-2.5 w-2.5 rounded-full', COLUMN_BAR_COLOR[columnKey])} />
          <h3 className="text-xs font-semibold tracking-wider uppercase text-gray-700 dark:text-gray-300">
            {title}
          </h3>
        </div>
        <span className="text-xs font-medium text-gray-500 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md px-1.5 py-0.5">
          {cards.length}
        </span>
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-3">
        {cards.length === 0 ? (
          <div className="text-xs text-gray-400 italic text-center py-6 border border-dashed border-gray-200 dark:border-slate-700 rounded-lg">
            Aucune école
          </div>
        ) : (
          cards.map((card) => (
            <BoardCard
              key={`${columnKey}-${card.school.id}`}
              card={card}
              column={columnKey}
              onClick={onCardClick && (canOpenCard?.(card) ?? true) ? () => onCardClick(card) : undefined}
              assignSlot={renderAssign?.(card)}
              plannedSlot={renderPlanned?.(card)}
            />
          ))
        )}
      </div>
    </div>
  );
}
